/* eslint-disable react/prop-types */
/**
=========================================================
* Material Dashboard 2 React - v2.2.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useEffect, useRef, useState } from "react";

// @mui material components
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDAlert from "components/MDAlert";
import MDButton from "components/MDButton";
import MDSnackbar from "components/MDSnackbar";

// Material Dashboard 2 React example components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import Footer from "examples/Footer";
import Table from "@mui/material/Table";
import DataTable from "../../../examples/Tables/DataTable";
import data from "../../dashboard/components/Projects/data";
import useApi from "../../../components/Utils/api";
import MDInput from "../../../components/MDInput";
import toast from "react-hot-toast";
import startOfMonth from "date-fns/startOfMonth";
import addMonths from "date-fns/addMonths";
import endOfMonth from "date-fns/endOfMonth";
import {DatePicker, DateRangePicker} from "rsuite";
import { afterToday } from "rsuite/cjs/DateRangePicker/disabledDateUtils";
import reportsLineChartData from "../../dashboard/data/reportsLineChartData";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";
import Papa from "papaparse";

function TransactionSummary({ embedded = false, tabsSlot = null } = {}) {
  const { transactionSummarycolumns } = data();
  const { predefinedRanges } = reportsLineChartData;
  const [transactionSummary, setTransactionSummary] = useState([]);
  const [transactionSummaryFiltered, setTransactionSummaryFiltered] = useState([]);
  const [lastMonthDateRange, setLastMonthDateRange] = useState([new Date().toISOString().slice(0, 10),
    new Date().toISOString().slice(0, 10)]);
  const api = useApi();
  const [filterSearch, setFilterSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const formatDate = (date) => {
    const inputDate = new Date(date);
    const year = inputDate.getFullYear();
    const month = String(inputDate.getMonth() + 1).padStart(2, '0');
    const day = String(inputDate.getDate()).padStart(2, '0');
    return `${month}/${day}/${year}`;
  };
  const getTransactionSummary = (lastMonthDateRange) => {
    if(lastMonthDateRange == null) return;
    setLastMonthDateRange(lastMonthDateRange);
    const data = {
      startDate : formatDate(lastMonthDateRange[0]),
      endDate : formatDate(lastMonthDateRange[1])
    }
    api.post("transaction-summary", data)
      .then((response) => {
        setTransactionSummary(response.data.data.transactionSummary);
      })
      .catch((error) => {
        if (error.response && error.response.status === 403) {
          toast.error("You do not have permission to perform this action.", { id: "PermissionError" });
          return;
        }
        // No matching transactions is surfaced by the table's own empty state, not a toast
        setTransactionSummary([]);
      })
  }

  useEffect(() => {
    getTransactionSummary(lastMonthDateRange);
  }, []);

  useEffect(() => {
    let result = [...transactionSummary];
    const search = filterSearch.trim().toLowerCase();
    if (search) {
      result = result.filter(t => {
        const searchableValues = [
          t.cardAcceptorCode,
          t.merchantName,
          t.amount,
          t.amount != null ? (t.amount / 100).toFixed(2) : null,
          t.settlementStatus,
        ];
        return searchableValues.some(v => v != null && String(v).toLowerCase().includes(search));
      });
    }
    if (filterStatus) {
      result = result.filter(t => t.status === filterStatus);
    }
    setTransactionSummaryFiltered(result);
  }, [transactionSummary, filterSearch, filterStatus]);

  const resetFilters = () => {
    setFilterSearch("");
    setFilterStatus("");
  };

  const handleDownloadCSV = () => {
    if (transactionSummaryFiltered.length === 0) {
      toast.error("No records found to download", { id: "NoRecordFound" });
      return;
    }
    const headers = transactionSummarycolumns.map((c) => c.Header);
    const values = transactionSummaryFiltered.map((row) =>
      transactionSummarycolumns.map((c) =>
        typeof c.accessor === "function" ? c.accessor(row) : row[c.accessor]
      )
    );
    const csv = Papa.unparse([headers, ...values], { quotes: true });
    const ts = new Date().toISOString().slice(0, 19).replace(/:/g, "-");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `TransactionSummary_${ts}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const content = (
      <MDBox mt={3} mb={3}>
        <Grid container spacing={3} justifyContent="center">
          <Grid item xs={12}>
            <Grid container alignItems="center">
              <Grid item xs={4}>
                <MDBox>
                  <MDTypography variant="h5" fontWeight="medium">
                    Reports
                  </MDTypography>
                  <MDTypography variant="button" fontWeight="regular" color="secondary">
                    {transactionSummaryFiltered.length} Transactions
                  </MDTypography>
                </MDBox>
              </Grid>
              <Grid item xs={4} display="flex" justifyContent="center">
                <DateRangePicker
                    disabledDate={afterToday()}
                    ranges={predefinedRanges}
                    onChange={(date) => getTransactionSummary(date)}
                    format="d MMMM yyyy"
                    value={Object.keys(lastMonthDateRange).length !== 0 ? [new Date(lastMonthDateRange[0]), new Date(lastMonthDateRange[1])] : []}
                />
              </Grid>
              <Grid item xs={4} display="flex" justifyContent="flex-end">
                <MDButton variant="gradient" color="info" onClick={handleDownloadCSV}>
                  Export CSV
                </MDButton>
              </Grid>
            </Grid>
          </Grid>
          {tabsSlot && (
            <Grid item xs={12}>
              {tabsSlot}
            </Grid>
          )}
          <Grid item xs={12} lg={12}>
            <MDBox sx={(theme) => ({ border: `1px solid ${theme.palette.grey[300]}`, borderRadius: "8px", background: theme.palette.white.main, overflow: "hidden" })}>
            {/* Filter Panel */}
            <MDBox px={2} py={1.5} sx={(theme) => ({ borderBottom: `1px solid ${theme.palette.grey[300]}`, background: theme.palette.background.hover })}>
              <Grid container spacing={1} alignItems="center">
                <Grid item xs={12} sm={6} md={3} lg={true}>
                  <TextField
                    label="Search"
                    placeholder="Merchant Name, Code, Amount, Settlement Status..."
                    value={filterSearch}
                    onChange={(e) => setFilterSearch(e.target.value)}
                    fullWidth size="small" variant="outlined"
                    InputProps={{ style: { fontSize: "0.8rem" } }}
                    InputLabelProps={{ style: { fontSize: "0.8rem" } }}
                    sx={{ "& .MuiOutlinedInput-root": { height: "38px" } }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3} lg={true}>
                  <TextField
                    select
                    label="Status"
                    value={filterStatus}
                    onChange={(e) => setFilterStatus(e.target.value)}
                    fullWidth size="small" variant="outlined"
                    InputProps={{ style: { fontSize: "0.8rem" } }}
                    InputLabelProps={{ style: { fontSize: "0.8rem" } }}
                    sx={{ "& .MuiOutlinedInput-root": { height: "38px" }, "& .MuiSelect-select": { fontSize: "0.8rem" } }}
                  >
                    <MenuItem value="" style={{ fontSize: "0.8rem" }}>All</MenuItem>
                    <MenuItem value="INITIATED" style={{ fontSize: "0.8rem" }}>INITIATED</MenuItem>
                    <MenuItem value="AUTHORIZED" style={{ fontSize: "0.8rem" }}>APPROVED</MenuItem>
                    <MenuItem value="FAILED" style={{ fontSize: "0.8rem" }}>FAILED</MenuItem>
                  </TextField>
                </Grid>
                <Grid item xs="auto">
                  <MDButton variant="outlined" color="secondary" size="small" onClick={resetFilters}
                    sx={{ height: "38px", fontSize: "0.8rem", whiteSpace: "nowrap" }}>
                    Reset
                  </MDButton>
                </Grid>
              </Grid>
            </MDBox>
            <DataTable
                table={{
                  columns: transactionSummarycolumns,
                  rows: transactionSummaryFiltered,
                }}
              showTotalEntries={true}
              isSorted={true}
              noEndBorder
              canSearch={false}
              entriesPerPage={true}
              entriesPerPagePosition="bottom"
            />
            </MDBox>
          </Grid>
        </Grid>
      </MDBox>
  );

  if (embedded) return content;

  return (
    <DashboardLayout>
      {content}
      <Footer />
    </DashboardLayout>
  );
}

export default TransactionSummary;
